import { type UseQueryOptions } from '@tanstack/react-query';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import {
	type LWApiPublicBillingPaymentInstrumentDetailsResultRaw,
	type LWApiPublicBillingPaymentInstrumentProcessorRaw,
	type LWApiPublicBillingPaymentInstrumentTypeRaw,
	type LWApiPublicBillingPaymentInstrumentListResultRaw,
	type LWApiPublicBillingPaymentInstrumentListParamsRaw,
} from '@liquidweb-dev/club-sauce/public/billing/payment/instrument/index.raw';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import useBillingPaymentInstrumentList from 'modules/queries/billing/payment/instrument/useList';
import describePaymentMethod, {
	getBrand,
	getLast4,
} from 'modules/paymentMethod/describePaymentMethod';
import getPaymentMethodStatus, {
	getExpiresAt,
} from 'modules/paymentMethod/getPaymentMethodStatus';

/** extract payment metadata from a payment instrument */
const pickPaymentMetadata = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsResultRaw,
): {
	issuer?: string;
	expirationDate?: string;
	maskedNumber?: string;
	description?: string;
	paymentProfileId?: number;
} => {
	const issuer = getBrand(instrument);
	const expirationDate = getExpiresAt(instrument);
	const maskedNumber = getLast4(instrument);

	if (instrument.processor === 'authorize_net') {
		return {
			issuer,
			expirationDate,
			maskedNumber,
			description:
				instrument.description || instrument.metadata.ach_account_desc,
			// the profile a payment is charged against, held under one key for a
			// card and another for ACH
			paymentProfileId: Number(
				instrument.metadata.payment_profile_id ||
					instrument.metadata.ach_payment_profile_id,
			),
		};
	}

	if (instrument.processor === 'stripe') {
		return {
			issuer,
			expirationDate,
			maskedNumber,
		};
	}

	return {};
};

export interface ResolvedPaymentMethodI {
	description?: string;
	expirationDate?: string;
	instrumentId: number;
	isBackup: boolean;
	isDefault: boolean;
	/** the Billing list flags the same method as expired, and it cannot be paid with */
	isExpired: boolean;
	issuer?: string;
	/** the row label the Billing list gives this method, e.g. `•••• •••• •••• 1117` */
	label: string;
	maskedNumber?: string;
	paymentMethod: LWApiPublicBillingPaymentInstrumentTypeRaw;
	/** the Authorize.Net profile a payment is charged against */
	paymentProfileId?: number;
	processor: LWApiPublicBillingPaymentInstrumentProcessorRaw;
	/** the security code is asked for again on every payment */
	requiresCVV: boolean;
	/** the quieter label after `label`, the bank name on a bank method */
	secondaryLabel?: string;
}

/** flatten one payment instrument into what the UI pays with */
const resolvePaymentMethod = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsResultRaw,
): ResolvedPaymentMethodI => {
	const {
		issuer,
		expirationDate,
		maskedNumber,
		description,
		paymentProfileId,
	} = pickPaymentMetadata(instrument);
	const { label, secondaryLabel } = describePaymentMethod(instrument);

	return {
		description,
		expirationDate,
		instrumentId: instrument.id,
		isBackup: fromApiBoolean(instrument.is_backup),
		isDefault: fromApiBoolean(instrument.is_default),
		isExpired: getPaymentMethodStatus({ instrument }).status === 'expired',
		issuer,
		label,
		maskedNumber,
		paymentMethod: instrument.type,
		paymentProfileId,
		processor: instrument.processor,
		requiresCVV:
			instrument.type === 'credit_card' && instrument.processor !== 'stripe',
		secondaryLabel,
	};
};

export interface UsePaymentMethodsOptions {
	defaultOnly?: boolean;
	payableOnly?: boolean;
	instrumentListQueryOptions?: UseQueryOptions<
		LWApiPublicBillingPaymentInstrumentListResultRaw,
		ApiErrorI
	>;
	instrumentListParams?: LWApiPublicBillingPaymentInstrumentListParamsRaw;
}

/** The account's payment methods, the default first and the backup second. */
const usePaymentMethods = ({
	/** fetches the account default alone, for a caller with no use for the rest */
	defaultOnly = false,
	/**
	 * Leaves out the methods a payment cannot be taken with — a check kept on
	 * file to settle bills another way, for one. Opt in where the list is
	 * something to pay with; leave it off where the list is something to show.
	 */
	payableOnly = false,
	instrumentListQueryOptions,
	instrumentListParams = {},
}: UsePaymentMethodsOptions = {}) => {
	const { alsowith: callerAlsowith, ...listParams } = instrumentListParams;

	// issuer, expiry and last four are read from `cardDetails`, so it is always asked
	// for, with whatever else the caller wants alongside it
	const alsowith = [
		'cardDetails',
		...[callerAlsowith || []].flat().filter((name) => name !== 'cardDetails'),
	];

	const instrumentListQuery = useBillingPaymentInstrumentList(
		defaultOnly
			? { is_default: 1, ...listParams, alsowith }
			: { ...listParams, alsowith },
		instrumentListQueryOptions,
	);

	const instruments = instrumentListQuery.data?.items;

	const defaultInstrument = instruments?.find(
		(instrument) => instrument.is_default,
	);

	const defaultPaymentMethod = defaultInstrument
		? resolvePaymentMethod(defaultInstrument)
		: undefined;

	// already sorted default first, then backup, by the list query
	const paymentMethods = (instruments || [])
		.map(resolvePaymentMethod)
		.filter((method) => !payableOnly || method.paymentMethod !== 'check');

	return {
		defaultPaymentMethod,
		paymentMethods,

		instrumentListQuery,
	};
};

export default usePaymentMethods;
